import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import { getDataAPI, postDataAPI } from '../../utils/fetchData';

const HotelSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modalConfig, showModal, closeModal } = useModal();
  
  const [shows, setShows] = useState([]);
  const [selectedShows, setSelectedShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // Tour plan context
  const [tourPlanId, setTourPlanId] = useState(null);
  const [tourPlanInfo, setTourPlanInfo] = useState(null);

  // Search criteria
  const [searchLocation, setSearchLocation] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);

  useEffect(() => {
    // If coming from ViewAll with pre-selected shows - set them immediately
    if (location.state?.selectedShows && location.state.selectedShows.length > 0) {
      const preSelectedShows = location.state.selectedShows;
      
      // Check if they're already full objects or just IDs
      if (preSelectedShows[0]?._id) {
        // Already full show objects - set them now
        console.log('🎯 Setting pre-selected shows:', preSelectedShows);
        setSelectedShows(preSelectedShows);
        // Auto-fill will happen via the selectedShows useEffect
      }
    }
    
    // Check if coming from a tour plan
    if (location.state?.tourPlanId) {
      setTourPlanId(location.state.tourPlanId);
      loadTourPlanContext(location.state.tourPlanId).then(() => {
        // Load shows after tour plan context is loaded
        loadUpcomingShows();
      });
    } else {
      // No tour plan, just load shows normally
      loadUpcomingShows();
    }
  }, []);
  
  // Reload shows when tour plan info changes
  useEffect(() => {
    if (tourPlanInfo) {
      loadUpcomingShows();
    }
  }, [tourPlanInfo]);
  
  const loadTourPlanContext = async (planId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI(`user/tm/tour-plans/${planId}`, token);
      
      if (response.data.status === 200) {
        const plan = response.data.data.tourPlan;
        setTourPlanInfo({
          name: plan.name,
          hotels: plan.hotels,
          showIds: plan.shows.map(show => show._id || show) // Extract show IDs
        });
      }
    } catch (error) {
      console.error('Error loading tour plan context:', error);
    }
  };

  const loadUpcomingShows = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/tm/shows', token);
      
      console.log('HotelSearch - API Response:', response.data);
      
      if (response.data.status === 200 && response.data.data) {
        const allShows = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        
        let upcomingShows = allShows.filter(show => 
          !['completed', 'cancelled'].includes(show.status)
        );
        
        // If we have a tour plan, filter to only shows in that plan
        if (tourPlanInfo?.showIds) {
          console.log('Filtering shows for tour plan:', tourPlanInfo.showIds);
          upcomingShows = upcomingShows.filter(show => 
            tourPlanInfo.showIds.includes(show._id)
          );
          console.log('Filtered shows:', upcomingShows);
        }
        
        console.log('HotelSearch - Upcoming shows:', upcomingShows);
        setShows(upcomingShows);
        
        // Auto-select first show ONLY if:
        // 1. No shows currently selected
        // 2. No pre-selected shows were passed via location.state
        const hasPreSelectedShows = location.state?.selectedShows && location.state.selectedShows.length > 0;
        if (selectedShows.length === 0 && upcomingShows.length > 0 && !hasPreSelectedShows) {
          console.log('🔄 Auto-selecting first show (no pre-selection)');
          setSelectedShows([upcomingShows[0]]);
        } else if (hasPreSelectedShows && selectedShows.length > 0) {
          console.log('✅ Keeping pre-selected shows:', selectedShows.length);
        }
      }
    } catch (error) {
      console.error('Error loading shows:', error);
      showModal('Error', 'Failed to load shows', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (selectedShows.length === 0) {
      showModal('No Shows Selected', 'Please select at least one show to search hotels for.', 'warning');
      return;
    }

    // Auto-populate from first selected show if fields are empty
    if (selectedShows.length > 0) {
      const firstShow = selectedShows[0];
      autoPopulateFromShow(firstShow);
    }

    // Get current values with fallback to show data
    const currentLocation = searchLocation || selectedShows[0]?.venue?.city || '';
    const currentCheckIn = checkInDate || selectedShows[0]?.showDate || '';
    const currentCheckOut = checkOutDate || (() => {
      if (selectedShows[0]?.showDate) {
        const nextDay = new Date(selectedShows[0].showDate);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay.toISOString().split('T')[0];
      }
      return '';
    })();

    if (!currentLocation) {
      showModal('Location Required', 'Please enter a city or location.', 'warning');
      return;
    }

    if (!currentCheckIn || !currentCheckOut) {
      showModal('Dates Required', 'Please select check-in and check-out dates.', 'warning');
      return;
    }

    try {
      setSearching(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));

      // For multiple shows, we'll search hotels for each location
      const allHotels = [];
      const errors = [];
      
      for (const show of selectedShows) {
        const showCity = searchLocation || show.venue?.city || '';
        
        // Skip shows with invalid cities
        if (!showCity || showCity.toLowerCase() === 'unknown' || showCity.toLowerCase() === 'tbd' || showCity.toLowerCase().includes('unknown')) {
          console.log(`⚠️ Skipping show "${show.venue?.name}" - invalid city: ${showCity}`);
          errors.push({
            show: show.venue?.name || 'Unknown Venue',
            reason: 'Invalid or missing city'
          });
          continue;
        }
        
        // Format dates properly - use form values if set, otherwise fallback to show date
        const formatDateForAPI = (dateValue) => {
          if (!dateValue) return '';
          if (typeof dateValue === 'string' && dateValue.includes('T')) {
            // Already ISO format, extract date part
            return dateValue.split('T')[0];
          }
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        };
        
        const showCheckIn = checkInDate || show.showDate;
        const showCheckOut = checkOutDate || (() => {
          if (show.showDate) {
            const nextDay = new Date(show.showDate);
            nextDay.setDate(nextDay.getDate() + 1);
            return nextDay.toISOString().split('T')[0];
          }
          return '';
        })();
        
        const searchParams = {
          show_id: show._id,
          location: showCity,
          check_in_date: formatDateForAPI(showCheckIn),
          check_out_date: formatDateForAPI(showCheckOut),
          guests: guests,
          rooms: rooms
        };

        console.log(`🏨 Searching hotels for show: ${show.venue?.name || 'Unknown'}`);
        console.log('Search params:', searchParams);

        try {
          const response = await postDataAPI('user/tm/hotels/search', searchParams, token);
          
          if (response.data.status === 200 && response.data.data?.hotels) {
            const hotelsWithShowInfo = response.data.data.hotels.map(hotel => ({
              ...hotel,
              showId: show._id,
              showName: show.venue?.name || 'Unknown Venue',
              showDate: show.showDate
            }));
            
            allHotels.push(...hotelsWithShowInfo);
            console.log(`✅ Found ${response.data.data.hotels.length} hotels for ${show.venue?.name}`);
          } else {
            console.log(`⚠️ No hotels found for ${show.venue?.name}`);
            errors.push({
              show: show.venue?.name || 'Unknown Venue',
              reason: 'No hotels found'
            });
          }
        } catch (error) {
          console.error(`Error searching hotels for ${show.venue?.name}:`, error);
          errors.push({
            show: show.venue?.name || 'Unknown Venue',
            reason: error.response?.data?.message || 'Search failed'
          });
        }
      }

      setSearchResults(allHotels);

      if (errors.length > 0 && allHotels.length === 0) {
        const errorMessages = errors.map(e => `${e.show}: ${e.reason}`).join('\n');
        showModal(
          'Search Issues',
          `Could not search hotels:\n${errorMessages}`,
          'warning'
        );
      } else if (allHotels.length === 0) {
        showModal('No Hotels Found', 'No hotels found for your search criteria. Try adjusting your dates or location.', 'info');
      } else {
        showModal('Search Complete', `Found ${allHotels.length} hotel options for ${selectedShows.length} show(s) via web search.`, 'success');
      }
    } catch (error) {
      console.error('Error searching hotels:', error);
      showModal('Search Error', 'Failed to search hotels. Please try again.', 'error');
    } finally {
      setSearching(false);
    }
  };

  const handleAddHotel = async (hotel) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      console.log('🏨 Adding hotel to show:', hotel.showName);
      console.log('Hotel data being sent:', hotel);
      
      const hotelData = {
        show_id: hotel.showId,
        name: hotel.name,
        brand: hotel.brand,
        logo: hotel.logo,
        address: hotel.address,
        city: hotel.city,
        check_in_date: hotel.check_in_date || checkInDate,
        check_out_date: hotel.check_out_date || checkOutDate,
        price_per_night: hotel.price_per_night,
        total_price: hotel.total_price,
        amenities: hotel.amenities || [],
        link: hotel.link
      };

      const response = await postDataAPI('user/tm/hotels', hotelData, token);
      
      console.log('Add hotel response:', response.data);
      
      if (response.data.status === 200 || response.data.status === 201) {
        showModal('Hotel Added', `Hotel has been added to ${hotel.showName}!`, 'success');
        // Don't auto-navigate - let user continue searching or use Save & Close button
      } else {
        console.error('Unexpected response status:', response.data);
        showModal('Error', response.data.message || 'Failed to add hotel. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding hotel:', error);
      console.error('Error details:', error.response?.data || error.message);
      showModal('Error', error.response?.data?.message || 'Failed to add hotel. Please try again.', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const formatPrice = (amount) => {
    if (!amount || amount === 0) return 'Price not available';
    return `$${amount.toLocaleString()}`;
  };

  // Helper function to auto-populate search fields from a show
  const autoPopulateFromShow = (show) => {
    // Auto-populate city if empty and show has city
    setSearchLocation(prev => {
      if (!prev && show.venue?.city) {
        return show.venue.city;
      }
      return prev;
    });
    
    // Auto-populate check-in date if empty and show has date
    setCheckInDate(prev => {
      if (!prev && show.showDate) {
        const showDateStr = new Date(show.showDate).toISOString().split('T')[0];
        return showDateStr;
      }
      return prev;
    });
    
    // Auto-populate check-out date if empty and show has date
    setCheckOutDate(prev => {
      if (!prev && show.showDate) {
        const checkOutDateObj = new Date(show.showDate);
        checkOutDateObj.setDate(checkOutDateObj.getDate() + 1);
        return checkOutDateObj.toISOString().split('T')[0];
      }
      return prev;
    });
  };

  // Auto-populate search fields when selectedShows changes
  useEffect(() => {
    if (selectedShows.length > 0) {
      // Use the first selected show to auto-populate
      const firstShow = selectedShows[0];
      autoPopulateFromShow(firstShow);
    }
  }, [selectedShows]);

  const toggleShowSelection = (show) => {
    if (selectedShows.find(s => s._id === show._id)) {
      setSelectedShows(selectedShows.filter(s => s._id !== show._id));
    } else {
      const newSelectedShows = [...selectedShows, show];
      setSelectedShows(newSelectedShows);
      // Auto-populate from the newly selected show if fields are empty
      autoPopulateFromShow(show);
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <div className="flex flex-col gap-1">
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Hotel Search</span>
              {tourPlanInfo && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 bg-[rgba(147,51,234,0.2)] text-purple-400 rounded border border-[rgba(147,51,234,0.3)]">
                    📋 {tourPlanInfo.name}
                  </span>
                  <button
                    onClick={() => navigate(`/tour-manager/tour-overview/${tourPlanId}`)}
                    className="text-[10px] text-[rgba(255,255,255,0.5)] hover:text-white underline"
                  >
                    View Plan
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (tourPlanId) {
                    navigate(`/tour-manager/tour-overview/${tourPlanId}`, {
                      state: { fromHotelSearch: true }
                    });
                  } else {
                    navigate('/tour-manager/view-all');
                  }
                }}
                className="px-3 py-1.5 bg-[#FF9500] text-white text-[12px] rounded hover:bg-[#FF7700] transition-colors"
              >
                Save & Close
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Back
              </span>
            </div>
          </div>

          {/* Tour Context Banner */}
          {tourPlanId && selectedShows.length > 1 && (
            <div className="w-full px-4 mb-3">
              <div className="bg-[rgba(147,51,234,0.1)] border border-[rgba(147,51,234,0.3)] rounded-lg p-3 text-center">
                <span className="text-purple-400 text-[11px]">
                  🗺️ Booking travel for your tour ({selectedShows.length} shows)
                </span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              <div className="text-center py-[60px]">
                <p className="text-white text-[12px]">Loading shows...</p>
              </div>
            ) : shows.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No upcoming shows
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  You need upcoming shows to search for hotels. Add shows first.
                </p>
              </div>
            ) : (
              <>
                {/* Show Selection */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white text-[12px] font-semibold">
                      Select Shows ({selectedShows.length} selected)
                    </h3>
                    {selectedShows.length > 0 && (
                      <button
                        onClick={() => setSelectedShows([])}
                        className="text-[#FF9500] text-[10px] font-semibold cursor-pointer hover:opacity-70"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-3">
                    Select multiple shows to search hotels for each location
                  </p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {shows.map(show => {
                      const isSelected = selectedShows.find(s => s._id === show._id);
                      return (
                        <div
                          key={show._id}
                          onClick={() => toggleShowSelection(show)}
                          className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-3 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-[#FF9500] bg-[rgba(255,149,0,0.1)]'
                              : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.3)]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'bg-[#FF9500] border-[#FF9500]' : 'border-[rgba(255,255,255,0.3)]'
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-white text-[11px] font-semibold">
                                {show.venue?.name || 'Unknown Venue'}
                              </p>
                              <p className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                                {show.venue?.city || 'Location TBD'} • {formatDate(show.showDate)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedShows.length > 1 && (
                    <div className="mt-3 bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded-lg p-3">
                      <p className="text-[#FF9500] text-[10px] font-semibold mb-1">
                        🏨 Multi-City Tour Search
                      </p>
                      <p className="text-[rgba(255,255,255,0.6)] text-[9px]">
                        Searching hotels for {selectedShows.length} shows across{' '}
                        {[...new Set(selectedShows.map(s => s.venue?.city).filter(Boolean))].length} location(s)
                      </p>
                    </div>
                  )}
                </div>

                {/* Search Criteria */}
                {selectedShows.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white text-[12px] font-semibold mb-3">
                      Search Criteria
                    </h3>
                    {selectedShows.length > 1 && (
                      <div className="mb-3 bg-[rgba(52,199,89,0.1)] border border-[rgba(52,199,89,0.3)] rounded-lg p-2">
                        <p className="text-[rgba(255,255,255,0.7)] text-[9px]">
                          💡 Hotels will be searched for each show's location automatically
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      <div>
                        <label className="text-[rgba(255,255,255,0.7)] text-[10px] block mb-1">
                          Location {selectedShows.length === 1 ? '' : '(optional override)'}
                        </label>
                        <input
                          type="text"
                          value={searchLocation}
                          onChange={(e) => setSearchLocation(e.target.value)}
                          placeholder={
                            selectedShows.length === 1 
                              ? "City or location" 
                              : "Leave empty to use each show's city"
                          }
                          className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] focus:border-[#FF9500] focus:outline-none placeholder-[rgba(255,255,255,0.4)]"
                        />
                        {selectedShows.length > 1 && !searchLocation && (
                          <p className="text-[rgba(255,255,255,0.5)] text-[9px] mt-1">
                            Searching in: {[...new Set(selectedShows.map(s => s.venue?.city).filter(Boolean))].join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[rgba(255,255,255,0.7)] text-[10px] block mb-1">
                            Check-in
                          </label>
                          <input
                            type="date"
                            value={checkInDate}
                            onChange={(e) => setCheckInDate(e.target.value)}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] focus:border-[#FF9500] focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-[rgba(255,255,255,0.7)] text-[10px] block mb-1">
                            Check-out
                          </label>
                          <input
                            type="date"
                            value={checkOutDate}
                            onChange={(e) => setCheckOutDate(e.target.value)}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] focus:border-[#FF9500] focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[rgba(255,255,255,0.7)] text-[10px] block mb-1">
                            Guests
                          </label>
                          <input
                            type="number"
                            value={guests}
                            onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                            min="1"
                            max="10"
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] focus:border-[#FF9500] focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-[rgba(255,255,255,0.7)] text-[10px] block mb-1">
                            Rooms
                          </label>
                          <input
                            type="number"
                            value={rooms}
                            onChange={(e) => setRooms(parseInt(e.target.value) || 1)}
                            min="1"
                            max="5"
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[11px] focus:border-[#FF9500] focus:outline-none"
                          />
                        </div>
                      </div>
                      <button
                        onClick={handleSearch}
                        disabled={searching}
                        className="w-full bg-[#FF9500] hover:bg-[#FF9500]/90 text-white border-none rounded-lg px-4 py-3 text-[12px] font-semibold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {searching ? 'Searching...' : 'Search Hotels'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white text-[12px] font-semibold mb-3">
                      Search Results ({searchResults.length} hotels)
                    </h3>
                    <div className="space-y-3">
                      {searchResults.map((hotel, index) => {
                        return (
                          <div
                            key={index}
                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden hover:border-[#FF9500] transition-all"
                          >
                            {/* Hotel Image */}
                            {hotel.logo && (
                              <div className="w-full h-32 overflow-hidden bg-[rgba(255,255,255,0.1)]">
                                <img
                                  src={hotel.logo}
                                  alt={hotel.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            
                            <div className="p-4">
                              {/* Hotel Header */}
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h4 className="text-white text-[12px] font-semibold mb-1">
                                    {hotel.name}
                                  </h4>
                                  {hotel.brand && (
                                    <p className="text-[rgba(255,255,255,0.5)] text-[10px] mb-1">
                                      {hotel.brand}
                                    </p>
                                  )}
                                  {hotel.rating && (
                                    <div className="flex items-center gap-1 mb-2">
                                      <span className="text-[#FF9500] text-[10px]">⭐</span>
                                      <span className="text-white text-[10px] font-semibold">
                                        {hotel.rating}
                                      </span>
                                      {hotel.reviews > 0 && (
                                        <span className="text-[rgba(255,255,255,0.5)] text-[10px]">
                                          ({hotel.reviews.toLocaleString()} reviews)
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="text-[#34C759] text-[14px] font-bold">
                                    {formatPrice(hotel.price_per_night)}
                                  </p>
                                  <p className="text-[rgba(255,255,255,0.5)] text-[9px]">
                                    per night
                                  </p>
                                  {hotel.total_price && (
                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px] mt-1">
                                      Total: {formatPrice(hotel.total_price)}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Hotel Address */}
                              {hotel.address && (
                                <p className="text-[rgba(255,255,255,0.6)] text-[10px] mb-2">
                                  📍 {hotel.address}
                                </p>
                              )}

                              {/* Amenities */}
                              {hotel.amenities && hotel.amenities.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {hotel.amenities.slice(0, 5).map((amenity, i) => (
                                    <span
                                      key={i}
                                      className="bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] px-2 py-1 rounded text-[9px]"
                                    >
                                      {amenity}
                                    </span>
                                  ))}
                                  {hotel.amenities.length > 5 && (
                                    <span className="text-[rgba(255,255,255,0.5)] text-[9px] px-2 py-1">
                                      +{hotel.amenities.length - 5} more
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Show Info */}
                              <div className="bg-[rgba(255,149,0,0.1)] border border-[rgba(255,149,0,0.3)] rounded px-2 py-1 mb-3">
                                <p className="text-[#FF9500] text-[9px] font-semibold">
                                  For: {hotel.showName}
                                </p>
                              </div>

                              {/* Actions */}
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddHotel(hotel)}
                                  className="flex-1 bg-[#FF9500] hover:bg-[#FF9500]/90 text-white border-none rounded-lg px-3 py-2 text-[10px] font-semibold cursor-pointer transition-colors"
                                >
                                  Add to Trip
                                </button>
                                {hotel.link && (
                                  <a
                                    href={hotel.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.15)] text-white border-none rounded-lg px-3 py-2 text-[10px] font-semibold text-center cursor-pointer transition-colors"
                                  >
                                    View Details
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default HotelSearch;

